export const MESSAGE_MAX_GRAPHEMES = 140;
export const NICKNAME_MAX_GRAPHEMES = 30;
export const MESSAGE_URL_MAX_LENGTH = 2_048;

const graphemeSegmenter = new Intl.Segmenter("tr", { granularity: "grapheme" });
const wordSegmenter = new Intl.Segmenter("tr", { granularity: "word" });
const urlPattern = /(?:https?:\/\/|www\.|\b[a-z0-9-]+\.(?:com|net|org|io|co|tr)\b)/iu;
const repeatedGraphemePattern = /(.)\1{9,}/u;

export type ValidationCode =
  | "MESSAGE_REQUIRED"
  | "MESSAGE_TOO_LONG"
  | "NICKNAME_TOO_LONG"
  | "URL_NOT_ALLOWED"
  | "REPEATED_SPAM"
  | "CONFIGURED_UNSAFE_PATTERN";

export interface SafetyPattern {
  id: string;
  pattern: RegExp;
  action: "reject" | "flag_urgent" | "flag_standard";
}

export interface ContentValidationResult {
  ok: boolean;
  message: string;
  nickname?: string;
  errors: ValidationCode[];
  safetySignals: Array<{ patternId: string; action: SafetyPattern["action"] }>;
}

export function normalizeUserText(value: string) {
  return value.normalize("NFKC").replace(/\r\n?/gu, "\n").trim();
}

export function graphemeCount(value: string) {
  return Array.from(graphemeSegmenter.segment(value)).length;
}

export function normalizeMessageUrl(rawUrl: string | null | undefined): string | null {
  const value = rawUrl?.trim() ?? "";
  if (!value || value.length > MESSAGE_URL_MAX_LENGTH) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (url.username || url.password) return null;
    return url.href.length <= MESSAGE_URL_MAX_LENGTH ? url.href : null;
  } catch {
    return null;
  }
}

export function isValidMessageUrlInput(rawUrl: string | null | undefined) {
  return !rawUrl?.trim() || normalizeMessageUrl(rawUrl) !== null;
}

function hasRepeatedWordSpam(value: string) {
  const words = Array.from(wordSegmenter.segment(value))
    .filter((part) => part.isWordLike)
    .map((part) => part.segment.toLocaleLowerCase("tr-TR"));

  let runLength = 1;
  for (let index = 1; index < words.length; index += 1) {
    runLength = words[index] === words[index - 1] ? runLength + 1 : 1;
    if (runLength >= 4) return true;
  }
  return false;
}

export function validatePublishContent(
  rawMessage: string,
  rawNickname = "",
  configuredPatterns: SafetyPattern[] = []
): ContentValidationResult {
  const message = normalizeUserText(rawMessage);
  const nickname = normalizeUserText(rawNickname);
  const errors: ValidationCode[] = [];
  const safetySignals: ContentValidationResult["safetySignals"] = [];

  if (graphemeCount(message) === 0) errors.push("MESSAGE_REQUIRED");
  if (graphemeCount(message) > MESSAGE_MAX_GRAPHEMES) errors.push("MESSAGE_TOO_LONG");
  if (graphemeCount(nickname) > NICKNAME_MAX_GRAPHEMES) errors.push("NICKNAME_TOO_LONG");
  if (urlPattern.test(message)) errors.push("URL_NOT_ALLOWED");
  if (repeatedGraphemePattern.test(message) || hasRepeatedWordSpam(message)) {
    errors.push("REPEATED_SPAM");
  }

  for (const configured of configuredPatterns) {
    configured.pattern.lastIndex = 0;
    if (!configured.pattern.test(message)) continue;
    safetySignals.push({ patternId: configured.id, action: configured.action });
    if (configured.action === "reject") errors.push("CONFIGURED_UNSAFE_PATTERN");
  }

  return {
    ok: errors.length === 0,
    message,
    ...(nickname ? { nickname } : {}),
    errors: [...new Set(errors)],
    safetySignals
  };
}
